import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { UltraluxUSPDefinitions } from 'constants/ultraLux'
import GeoContext from 'contexts/geoContext'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useContext } from 'react'
import styled, { keyframes } from 'styled-components'
import eases from 'styles/tools/eases'
import UltraLuxTransferModal from './UltraLuxTransferModal'
import ContactContext from 'contexts/contactContext'
import { TRANSFERS_USP_REGIONS } from 'constants/config/region'

const ItemTransition = keyframes`
  0% {
    transform: translateX(0);
  }

  28% {
    transform: translateX(0);
  }

  /* Transition to slide 2 */

  33% {
    transform: translateX(calc(-100% - ${rem(16)}));
  }

  61% {
    transform: translateX(calc(-100% - ${rem(16)}));
  }
  /* Transition to slide 3 */

  66% {
    transform: translateX(calc(-200% - ${rem(32)}));
  }

  94% {
    transform: translateX(calc(-200% - ${rem(32)}));
  }

  /* Transition to slide 1 */

  100% {
    transform: translateX(0);
  }
`

const BubbleTransition = keyframes`
  0% {
    width: ${rem(16)};
  }

  28% {
    width: ${rem(16)};
  }

  33% {
    width: ${rem(8)};
  }

  94% {
    width: ${rem(8)};
  }

  100% {
    width: ${rem(16)};
  }
`

const BubbleProgressTransition = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 1;
  }

  /* Slide across the bubble */

  28% {
    transform: translateX(0%);
    opacity: 1;
  }

  /* Fade out as we change slides */

  33% {
    transform: translateX(0%);
    opacity: 0;
  }

  /* Wait for our turn again */

  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`

const CarouselBubbles = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const CarouselBubble = styled.div`
  position: relative;
  height: ${rem(8)};
  width: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  animation: ${BubbleTransition} 9s infinite;
  border: 1px solid ${props => props.theme.palette.neutral.default.one};
  overflow: hidden;
  transform: translateZ(0);


  &:nth-child(2) {
    animation-delay: 3s;

    &::after {
      animation-delay: 3s;
    }
  }

  &:nth-child(3) {
    animation-delay: 6s;

    &::after {
      animation-delay: 6s;
    }
  }

  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    background-color: ${props => props.theme.palette.neutral.default.one};
    animation: ${BubbleProgressTransition} 9s infinite;
  }
`

const UltraLuxUSPs = styled.div`
  display: grid;
  gap: ${rem(20)};
  padding-top: ${rem(36)};
  padding-bottom: ${rem(36)};
  overflow: visible;

  ${mediaQueryUp.tablet} {
    animation: none;
    grid-template-columns: unset;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    padding-top: ${rem(56)};
  }

  &.carousel {
    animation: ${ItemTransition} 9s infinite ${eases.power2.inOut};
    grid-template-columns: repeat(3, 100%);

    ${mediaQueryUp.tablet} {
      animation: none;
      grid-template-columns: unset;
    }
  }
`

const UltraLuxUSP = styled.div`
  display: grid;
  gap: ${rem(32)};
  grid-template-columns: ${rem(120)} 1fr;
  align-items: center;
  height: ${rem(120)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-left: -${rem(16)};
  margin-right: -${rem(16)};
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};

  > ${mediaQueryUp.tablet} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }

  &.paused {
    ${UltraLuxUSPs} {
      animation-play-state: paused;
    }

    ${CarouselBubble} {
      /* Indicate a paused state by fading it a little */
      opacity: 0.6;
      transform: scale(0.8);
      animation-play-state: paused;

      &::after {
        animation-play-state: paused;
      }
    }
  }
`

interface Props {
  verticalType: 'hotels' | 'tours' | 'cruises';
}

function UltraLuxVerticalPageUSPs(props: Props) {
  const { verticalType } = props
  const currentContact = useContext(ContactContext)
  const contact = currentContact.defaultContact
  const [transferModalOpen, _, openTransferModal, closeTransferModal] = useToggle()

  const { currentRegionCode } = useContext(GeoContext)
  const canOfferTransfers = TRANSFERS_USP_REGIONS.includes(currentRegionCode)

  const premiumUsp = UltraluxUSPDefinitions[verticalType].premium(currentRegionCode)
  const serviceUsp = UltraluxUSPDefinitions[verticalType].service(currentRegionCode)
  const transferUsp = UltraluxUSPDefinitions[verticalType].transfers(currentRegionCode)

  return <>
    <AnimationContainer gap={20}>
      <UltraLuxUSPs className={canOfferTransfers ? 'carousel' : undefined}>
        <UltraLuxUSP>
          <Image fit="center" id={premiumUsp.imageId} height={120} dpr={2} />
          <VerticalSpacer gap={4}>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {premiumUsp.title}
            </BodyText>
            <BodyText variant="medium" colour="neutral-three">
              {premiumUsp.subtitle}
            </BodyText>
          </VerticalSpacer>
        </UltraLuxUSP>
        <UltraLuxUSP>
          <Image fit="center" id={serviceUsp.imageId} height={120} dpr={2} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" weight="bold" colour="neutral-two">
                {serviceUsp.title}
              </BodyText>
              <BodyText variant="medium" colour="neutral-three">
                {serviceUsp.subtitle}
              </BodyText>
            </VerticalSpacer>
            <TextLink size="medium" href={`tel:${contact.number}`}>
              Call {contact.humanReadable}
            </TextLink>
          </VerticalSpacer>
        </UltraLuxUSP>
        {canOfferTransfers && <UltraLuxUSP>
          <Image fit="center" id={transferUsp.imageId} height={120} dpr={2} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" weight="bold" colour="neutral-two">
                {transferUsp.title}
              </BodyText>
              <BodyText variant="medium" colour="neutral-three">
                {transferUsp.subtitle}
              </BodyText>
            </VerticalSpacer>
            <TextLink size="medium" onClick={openTransferModal}>
              Learn more
            </TextLink>
          </VerticalSpacer>
        </UltraLuxUSP>}
      </UltraLuxUSPs>
      {canOfferTransfers && <CarouselBubbles>
        <CarouselBubble />
        <CarouselBubble />
        <CarouselBubble />
      </CarouselBubbles>}
    </AnimationContainer>
    <UltraLuxTransferModal
      open={transferModalOpen}
      onClose={closeTransferModal}
    />
  </>
}

export default UltraLuxVerticalPageUSPs
